/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import { Component, Vue } from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {GameInterface} from '@/store/modules/games/types';
import {IssueInterface, IssuesConfigInterface, IssueTitleType} from '@/store/modules/issues/types';
import IssueTitleInfo from '@/components/IssueTitleInfo/scripts';

/**
 * Search component.
 *
 * @author jgeorgiadou@frontier.co.uk
 */
@Component({
    name: 'c-search',
    components: {
        'c-issue-title-info': IssueTitleInfo,
    },
})
export default class Search extends Vue {

    /**
     * Issues Getter
     */
    public get issues(): IssueInterface[] {
        return this.getIssues('searchIssues');
    }
    public selectedGameId: number | null = null;
    public term: string = '';
    public typeList: IssueTitleType = IssueTitleType.LIST;
    public selectedGameVersion: number | null = null;
    public gameVersions: any = [];

    // Issues
    @Action('fetchIssues', {namespace: 'issuesStateModule'})
    public fetchIssues!: (issuesConfig: IssuesConfigInterface) => Promise<void>;
    @Action('updateSearchTerm', {namespace: 'issuesStateModule'})
    public updateSearchTerm!: (term: string) => void;
    @Getter('getIssues', {namespace: 'issuesStateModule'})
    public getIssues!: (stateName: string) => IssueInterface[];
    @Action('updateCurrentGame', {namespace: 'gamesStateModule'})
    public updateCurrentGame!: (game: GameInterface | undefined) => void;
    @Action('updateCurrentGameVersion', {namespace: 'gamesStateModule'})
    public updateCurrentGameVersion!: (version: number | null) => void;
    @Getter('getCurrentGame', {namespace: 'gamesStateModule'})
    public getCurrentGame!: GameInterface;
    @Getter('getGames', {namespace: 'gamesStateModule'})
    public games!: GameInterface[];
    // Games
    @Action('fetchGames', {namespace: 'gamesStateModule'})
    public fetchGames!: () => Promise<void>;
    // Settings masthead
    @Action('updateMastheadImage', {namespace: 'settingsModule'})
    public updateMastheadImage!: (imageSlug: string | undefined) => void;

    public debounceInput: Debounce = this.$debounce(this.keywordChanged, 500);
    private showResults: boolean = false;

    /**
     * Get versions of the selected game
     */
    public getSelectedGameVersions(): void {
        for (const game of this.games) {
            if (game.id === this.selectedGameId) {
                this.gameVersions = game.versions;
            }
        }
    }

    /**
     * Keyword changed event.
     * @param {string} keyword
     */
    public async keywordChanged(keyword?: string): Promise<void> {
        try {
            if (this.term.length < 3) {
                throw new Error('search string too short');
            }
            await this.fetchIssues({
                filters: {
                    searchTerm: this.term,
                    gameId: this.selectedGameId,
                    version: this.selectedGameVersion,
                },
                stateName: 'search',
                showIssueSummary: true,
            });
            this.showResults = this.issues && this.issues.length > 0 && this.term !== '';
        } catch (e) {
            this.showResults = false;
        }
    }

    /**
     * Mounted life-cycle event.
     *
     * Initialises the loader.
     */
    public async mounted(): Promise<void> {
        await this.fetchGames();
    }

    /**
     * Update the currentGame,
     * Update the search term and
     * Go to Reported Issues page
     */
    public goToReportedIssues(): void {
        const game: GameInterface | undefined = this.games.find((item: GameInterface): any => {
            return item.id === this.selectedGameId;
        });
        this.updateCurrentGame(game);
        this.updateSearchTerm(this.term);
        this.updateCurrentGameVersion(this.selectedGameVersion);

        // undefined key art reverts masthead to default Frontier image.
        const keyArt: string|undefined = this.getKeyArt(game);
        this.updateMastheadImage(keyArt);

        undefined === game
            ? this.$router.push({name: 'reported-issues'})
            : this.$router.push({
                    name: 'reported-issues-game-slug',
                    params: {gameSlug: game.slug},
                });
    }

    private getKeyArt(game: GameInterface|undefined): string|undefined {
        if (game !== undefined && game.branding !== null && game.branding.key_art !== null) {
            return game.branding.key_art.url;
        }
        return undefined;
    }
}

